import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { PaymentsDataServiceProvider } from '../../../../../shared/services/payments/payments-data-service/payments.data.service';
import {
	AmountWithCurrencyResponse,
	PageRequest,
	PaymentItem,
	PaymentsOverview
} from '../../../../../shared/models/models';
import { MessageService } from '../../../../../shared/services/helpers/message-service/message.service';
import { InvoiceServiceProvider } from '../../../../../shared/services/invoice/invoice-service/invoice.service';
import { Observable } from 'rxjs/Observable';
import { FormControl, Validators } from '@angular/forms';
import { FileDownloadDataServiceProvider } from '../../../../../shared/services/files/file-download-service/file-download.data.service';
import { PaginationService } from '../../../../../shared/services/pagination/pagination.service';
import { AccountServiceProvider } from '../../../../../shared/services/user/account.service';

@Component({
	selector: 'payments-overview',
	template: require('./payments-overview.component.html'),
	styles: [require('./payments-overview.component.scss')]
})
export class PaymentsOverviewComponent implements OnInit {
	@Output()
	openSendReceiptModalEvent: EventEmitter<any> = new EventEmitter<any>();

	readonly PAYMENTS_REQUEST_DEFAULT: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 25,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'paymentDate',
				sortAscending: true
			}
		],
		search: '',
		filterBy: 'unpaid'
	};

	pageRequest: PageRequest;
	paymentItems: Array<PaymentItem>;
	totalDue: string;
	isPaidSelected: string;
	paymentSearch: FormControl;
	itemsQuantity: number;
	pageNumbers: Array<number>;
	companyId: number;
	selectedPayment;

	constructor(
		private paymentsDataService: PaymentsDataServiceProvider,
		private invoiceService: InvoiceServiceProvider,
		private fileDownloadDataService: FileDownloadDataServiceProvider,
		private paginationService: PaginationService,
		private messageService: MessageService,
		private accountService: AccountServiceProvider
	) {}

	ngOnInit(): void {
		this.getCompanyId();
		this.applyPageProductFromLocalStorage();
		this.getPaymentsAndTotalDue();
		this.initPaymentSearch();
		this.getCompanyId();
	}

	updatePayments(pageRequest: PageRequest = this.pageRequest): void {
		this.pageRequest = pageRequest;

		this.getPayments().subscribe(
			(data: PaymentsOverview): void => this.successGetPayments(data),
			error => this.messageService.showDefaultErrorMessage(error)
		);
	}

	initPaymentSearch(): void {
		this.paymentSearch = new FormControl('', [Validators.maxLength(255)]);
		this.paymentSearch.valueChanges
			.debounceTime(300)
			.distinctUntilChanged()
			.subscribe((res: string) => {
				this.pageRequest.search = res;
				this.pageRequest.paging.searchToken = null;
				this.updatePayments();
			});
	}

	getPayments(): Observable<PaymentsOverview> {
		return this.paymentsDataService.getPaymentsV1(this.pageRequest);
	}

	getTotalDue(): Observable<AmountWithCurrencyResponse> {
		return this.paymentsDataService.getTotalDueV1(this.isPaidSelected);
	}

	changePaymentsFilter(filter: string): void {
		this.isPaidSelected = filter;
		this.pageRequest.filterBy = filter;
		this.pageRequest.paging.searchToken = null;

		if (filter === 'unpaid') {
			this.pageRequest.sortCriterias[0].sortAscending = true;
			this.pageRequest.sortCriterias[0].fieldName = 'paymentDate';
		} else {
			this.pageRequest.sortCriterias[0].fieldName = 'paidOn';
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}

		this.getPaymentsAndTotalDue();
	}

	orderByField(filter: string): void {
		if (this.pageRequest.sortCriterias[0].fieldName === filter) {
			this.pageRequest.sortCriterias[0].sortAscending = !this.pageRequest
				.sortCriterias[0].sortAscending;
		} else {
			this.pageRequest.sortCriterias[0].fieldName = filter;
			this.pageRequest.sortCriterias[0].sortAscending = false;
		}

		this.pageRequest.paging.searchToken = null;
		this.pageRequest.paging.pageNumber = 0;

		this.updatePayments();
	}

	downloadPayments(): void {
		this.fileDownloadDataService.savePayments();
	}

	openRecordPaymentModal(payment: PaymentItem): void {
		this.selectedPayment = JSON.parse(JSON.stringify(payment));

		// RecordPaymentModalComponent use old version api
		// Need prepare payment before open modal

		this.selectedPayment.amount = payment.amountWithCurrency.amount;
		this.selectedPayment.currencySymbol =
			payment.amountWithCurrency.currencySymbol;

		$('#markAsPayedModal').modal('show');
	}

	openSendReciptModal(payment: PaymentItem): void {
		const paymentForEmit = {
			id: payment.id,
			email: payment.clientEmail
		};

		this.openSendReceiptModalEvent.emit(paymentForEmit);
	}

	private getCompanyId(): void {
		this.accountService.companyIdObservable.subscribe(
			(companyId: number) => (this.companyId = companyId)
		);
	}

	private getPaymentsAndTotalDue(): void {
		Observable.zip(this.getPayments(), this.getTotalDue()).subscribe(
			([payments, total]): void => {
				this.successGetPayments(payments);
				this.totalDue = total.data.amountWithCurrency;
			},
			error => this.messageService.showDefaultErrorMessage(error)
		);
	}

	private successGetPayments(data: PaymentsOverview): void {
		const { items, searchToken, totalSize } = JSON.parse(JSON.stringify(data));

		this.paymentItems = this.invoiceService.setFormattedDaysToDue(items);

		this.itemsQuantity = data ? totalSize : 0;

		this.pageRequest.paging.searchToken = searchToken;

		this.pageNumbers = this.paginationService.calculatePageNumbers(
			this.itemsQuantity,
			this.pageRequest.paging.pageSize
		);

		this.setPageProductToLocalStorage(this.pageRequest);
	}

	// LOCAL STORAGE
	private applyPageProductFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`SNPaymentsRequest_${this.companyId}`)
		);

		this.pageRequest = this.PAYMENTS_REQUEST_DEFAULT;
		this.isPaidSelected = 'unpaid';

		if (
			pageRequestObject &&
			pageRequestObject !== null &&
			pageRequestObject !== ''
		) {
			const { pageSize, filterBy } = pageRequestObject;

			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.filterBy = filterBy;
			this.isPaidSelected = filterBy;
			if (filterBy === 'paid') {
				this.pageRequest.sortCriterias[0].fieldName = 'paidOn';
				this.pageRequest.sortCriterias[0].sortAscending = false;
			}
		}
	}

	private setPageProductToLocalStorage(pageRequest: PageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			filterBy: this.isPaidSelected
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`SNPaymentsRequest_${this.companyId}`);
		localStorage.setItem(
			`SNPaymentsRequest_${this.companyId}`,
			pageRequestString
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/components/payments/payments-overview/payments-overview.component.ts